import React, { useState } from 'react';
import { User, Mail, Phone, Sparkles, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { api, friendlyError } from '../../lib/api';
import { track, EventTypes, getSessionId } from '../../lib/analytics';
import { useAuth } from '../../context/AuthContext';
import advisor from '../../lib/advisor';

/**
 * AdvisorCaptureForm — captura del lead comprador (colección `leads`).
 * No bloquea las recomendaciones. Ofrece siempre "seguir explorando".
 *
 * mode:
 *  - 'general'    → Punto A: tras las primeras recomendaciones.
 *  - 'contextual' → Punto B: tras Guardar decisión firme (con `interest`).
 */

// Construye el contexto del Advisor desde las respuestas del guided.
export function buildAdvisorContext(answers = {}) {
  const budget = typeof answers.budget === 'object' ? answers.budget?.max : answers.budget;
  return {
    objetivo: answers.objective ?? null,
    operation: answers.operation ?? null,
    ciudad: answers.city ?? null,
    zona: answers.neighborhood ?? null,
    presupuesto: budget ?? null,
    habitaciones: answers.beds ?? null,
    tipo: answers.property_type ?? null,
    preferencias: {
      must_haves: answers.must_haves ?? null,
      mobility_mode: answers.mobility_mode ?? null,
      work_mode: answers.work_mode ?? null,
      priorities: answers.priorities ?? null,
      move_horizon: answers.move_horizon ?? null,
      new_construction_pref: answers.new_construction_pref ?? null,
      expected_roi: answers.expected_roi ?? null,
      investment_horizon: answers.investment_horizon ?? null,
    },
  };
}

function intentFromOperation(op) {
  if (op === 'arriendo') return 'arriendo';
  return 'compra'; // venta / desconocido → comprar
}
export { intentFromOperation };

const COPY = {
  general: {
    title: '¿Quieres recibir estas recomendaciones?',
    body: 'Ya entendimos qué estás buscando. Déjanos tus datos y podremos enviarte los detalles de las propiedades que mejor encajan contigo y avisarte cuando encontremos nuevas opciones.',
    cta: 'Quiero recibir mis recomendaciones',
    dismiss: 'Seguir explorando sin registrarme',
  },
  contextual: {
    title: 'Guarda esta opción y te acompañamos',
    body: 'Anotaste esta como tu decisión firme. Déjanos tus datos y te enviamos la ficha completa y te avisamos si baja de precio o aparecen opciones similares.',
    cta: 'Enviarme esta opción',
    dismiss: 'Seguir explorando sin registrarme',
  },
};

export default function AdvisorCaptureForm({
  mode = 'general',
  answers = {},
  interest = null,
  onCaptured,
  onDismiss,
}) {
  const { user } = useAuth();
  const [values, setValues] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const copy = COPY[mode] || COPY.general;
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const valid =
    values.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) &&
    values.phone.trim().length >= 7;

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || loading) return;
    setError(''); setLoading(true);
    try {
      const res = await api.advisorCapture({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        intent: intentFromOperation(answers.operation),
        context: buildAdvisorContext(answers),
        advisor_context_id: advisor.getActiveContextId?.() || null,
        interest: interest || null,
        source: interest ? 'advisor_firm_pick' : 'advisor',
        user_id: user?.id || null,
        session_id: getSessionId(),
      });
      setDone(true);
      try { track(EventTypes.LEAD_CREATED, { source: 'advisor', lead_type: 'comprador', mode }); } catch { /* noop */ }
      onCaptured?.({
        lead_id: res?.lead_id,
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
      });
    } catch (err) {
      setError(friendlyError(err));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="mt-6 rounded-2xl border-2 border-habita/40 bg-habita-mint/50 p-6 text-center" data-testid="advisor-capture-success">
        <div className="w-12 h-12 rounded-full bg-habita flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-white" />
        </div>
        <p className="font-serif text-xl font-bold text-habita-dark mt-3">¡Listo! Te enviaremos tus recomendaciones</p>
        <p className="text-sm text-gray-700 mt-1.5">
          Un asesor de Habita te compartirá los detalles y te avisará cuando aparezcan nuevas opciones que encajen contigo.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-2xl border-2 border-habita/30 bg-white p-6 shadow-sm" data-testid="advisor-capture-form">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-habita flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-habita-dark" data-testid="advisor-capture-title">{copy.title}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{copy.body}</p>
        </div>
      </div>

      <div className="space-y-3 mt-5">
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <User className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="advisor-capture-name-input" type="text" value={values.name}
            onChange={(e) => set('name', e.target.value)} placeholder="Nombre completo"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <Mail className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="advisor-capture-email-input" type="email" value={values.email}
            onChange={(e) => set('email', e.target.value)} placeholder="Correo electrónico"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <Phone className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="advisor-capture-phone-input" type="tel" value={values.phone}
            onChange={(e) => set('phone', e.target.value)} placeholder="WhatsApp"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
      </div>

      {error && <p data-testid="advisor-capture-error" className="text-sm text-red-600 mt-3 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <button type="submit" disabled={!valid || loading} data-testid="advisor-capture-submit"
        className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-habita hover:opacity-90 text-white font-bold h-12 rounded-full text-base disabled:opacity-50 transition-opacity">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <>{copy.cta} <ArrowRight className="w-4 h-4" /></>}
      </button>

      <button type="button" onClick={onDismiss} data-testid="advisor-capture-dismiss"
        className="w-full mt-2 text-center text-sm text-gray-500 hover:text-habita-dark underline">
        {copy.dismiss}
      </button>
      <p className="text-xs text-gray-400 text-center mt-2">Usaremos tus datos solo para enviarte recomendaciones y contactarte. Puedes solicitar su eliminación cuando quieras.</p>
    </form>
  );
}
